import { Suspense, lazy, useId } from "react";
import clsx from "clsx";
import styles from "./CodeMirrorField.module.css";

const CodeMirrorInner = lazy(() => import("./CodeMirrorInner"));

interface CodeMirrorFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Syntax highlighting language; `"none"` keeps a plain editor. */
  language?: "html" | "none";
  label?: string;
  hint?: string;
  error?: string;
  height?: string;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}

/**
 * CodeMirror 6 wrapped in the app's form-field chrome (label, hint, error)
 * so it can sit next to adminer `Input`/`Textarea` without looking foreign.
 * The editor itself is lazy-loaded (heavy chunk). Used by the code-template
 * editor, the partials modal and `html`/raw-html block props.
 */
export function CodeMirrorField({
  value,
  onChange,
  language = "html",
  label,
  hint,
  error,
  height = "220px",
  placeholder,
  readOnly,
  className,
}: CodeMirrorFieldProps) {
  const id = useId();

  return (
    <div className={clsx(styles.field, className)}>
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      )}
      <div className={clsx(styles.editor, error && styles.editorError)}>
        <Suspense
          fallback={
            <div className={styles.loading} style={{ height }}>
              Loading editor…
            </div>
          }
        >
          <CodeMirrorInner
            id={id}
            value={value}
            onChange={onChange}
            language={language}
            height={height}
            placeholder={placeholder}
            readOnly={readOnly}
          />
        </Suspense>
      </div>
      {error && <div className={styles.error}>{error}</div>}
      {hint && <div className={styles.hint}>{hint}</div>}
    </div>
  );
}
